<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import AppSidebar from './AppSidebar.vue'
import PaywallModal from '@/components/customUi/LicenseLimitations/PaywallModal.vue'
import TutorialAgentLauncher from '@/components/customUi/TutorialAgent/TutorialAgentLauncher.vue'
import GlobalNavigationSearch from './GlobalNavigationSearch.vue'
import TutorialAgentAutoTagger from '@/components/customUi/TutorialAgent/TutorialAgentAutoTagger.vue'
import { useTranslateKey } from '@/lib/utils'
import { Routes, type RouteName } from '@/router/routes'
import type { TNKeys } from '@/components/lang/langHandler'
import { useUserStore } from '@/stores/userStore'

const props = withDefaults(
  defineProps<{
    tutorialOpen?: boolean
    tutorialWidth?: number
  }>(),
  {
    tutorialOpen: false,
    tutorialWidth: 360
  }
)

const route = useRoute()
const userStore = useUserStore()
const { translateKey } = useTranslateKey()
const autoTagRoot = ref<HTMLElement | null>(null)

const routeTitleKeyMap: Partial<Record<RouteName, TNKeys>> = {
  [Routes.ASSISTANT]: 'assistant',
  [Routes.IMAGE_STUDIO]: 'imageStudio',
  [Routes.HEARTBEAT]: 'heartbeat'
}

const pageTitleKey = computed(() => {
  if (typeof route.name !== 'string') return null
  return routeTitleKeyMap[route.name as RouteName] ?? null
})

const showAssistantLauncher = computed(() => {
  return userStore.isBetaAccess && route.name !== Routes.ASSISTANT
})

onMounted(() => {
  autoTagRoot.value = document.body
})

onUnmounted(() => {
  autoTagRoot.value = null
})
</script>
<template>
  <header
    class="bg-background fixed top-0 left-0 z-40 flex h-16 items-center justify-between border-b px-4 transition-[right] duration-200 md:pr-10"
    :style="{ right: props.tutorialOpen ? `${props.tutorialWidth}px` : '0px' }"
  >
    <AppSidebar />
    <div
      v-if="pageTitleKey"
      class="pointer-events-none absolute left-1/2 hidden -translate-x-1/2 md:block"
    >
      <h1 class="text-foreground/85 text-sm font-semibold tracking-[0.08em] uppercase">
        {{ translateKey(pageTitleKey) }}
      </h1>
    </div>
    <PaywallModal />
    <div id="navbar-actions" class="flex gap-2 md:gap-4">
      <GlobalNavigationSearch />
      <TutorialAgentLauncher v-if="showAssistantLauncher" />
    </div>
  </header>
  <TutorialAgentAutoTagger :root="autoTagRoot" scope-id="navbar-runtime" />
</template>
